import Table from 'src/components/Table';
import React, { useMemo, useRef, useState } from 'react';
import { useIsFetching, useMutation } from 'react-query';
import queryKeys from 'src/constants/queryKeys';
import { chatgenieAPI } from 'src/config/api';
import { queryClient } from 'src/config/queryClient';
import { useHistory } from 'react-router-dom';
import { Button, Space, Typography } from 'antd';
import ConfirmModal from 'src/components/ConfirmModal';
import useToast from 'src/hooks/useToast';
import parse from 'url-parse';
import { extractServerErrorMessage } from 'src/utils';
import { getColumns } from './config';
import { AgentHeader, ListHeader } from './style';
import Filters from './Filters';
import { SCOPE_ALL_CLIENTS } from './constants';
import { getGroupedIntentData } from './utils';
import IntentTemplatesModal from './Templates';
import EmptyListPlaceholder from './Placeholder';
import MasterIntent from './MasterIntent';

const defaultData = {
  data: [],
  meta: {}
};

const defaultFilters = {
  visibilityScope: SCOPE_ALL_CLIENTS,
  companyTypes: [],
  contactTypes: [],
  parentCompanies: [],
  topLevels: [],
  clients: []
};

const defaultQueryString = '?page=1';

const Agent = () => {
  const queryStringRef = useRef(defaultQueryString);
  const [data, setData] = useState(
    queryClient.getQueryData([queryKeys.GET_AGENTS, { queryString: queryStringRef.current }]) ||
      defaultData
  );
  const [confirmDeleteData, setConfirmDeleteData] = useState({
    id: null,
    isConfirming: false
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const tableQueryState = useRef('');
  const { toast } = useToast();
  const isFetching = useIsFetching({ queryKey: [queryKeys.GET_AGENTS] });
  const [errorMessage, setErrorMessage] = useState('');

  const showResetButton = useMemo(() => {
    return Object.values(filters).some(value => Array.isArray(value) && value.length > 0);
  }, [filters]);

  const rows = useMemo(() => {
    return {
      data: getGroupedIntentData(data.data, filters),
      meta: data.meta
    };
  }, [filters, data]);

  const getFilterQueryParams = (passedFilters = filters, withQuestionMark = false) => {
    if (passedFilters.visibilityScope === SCOPE_ALL_CLIENTS) {
      return '';
    }
    return parse.qs.stringify(passedFilters, {
      withQuestionMark
    });
  };
  const isPlaceholderVisible = useMemo(() => {
    const isDataEmpty = !data?.data?.length;
    const hasFetched = !!queryClient.getQueryData([
      queryKeys.GET_AGENTS,
      { queryString: defaultQueryString }
    ]);

    return (
      isDataEmpty && hasFetched && !isFetching && defaultQueryString === queryStringRef.current
    );
  }, [data, isFetching]);

  const getData = ({ queryString, passedFilters = filters }) => {
    tableQueryState.current = queryString;
    const fullQueryString = (queryString || '') + getFilterQueryParams(passedFilters, !queryString);
    queryStringRef.current = fullQueryString;
    const cachedData = queryClient.getQueryData([
      queryKeys.GET_AGENTS,
      { queryString: fullQueryString }
    ]);
    if (cachedData) setData(cachedData);
    queryClient
      .fetchQuery({
        queryKey: [queryKeys.GET_AGENTS, { queryString: fullQueryString }],
        queryFn: () =>
          chatgenieAPI.getAgentIntents({
            queryString: fullQueryString
          })
      })
      .then(res => {
        setErrorMessage('');
        setData(res);
      })
      .catch(err => {
        setErrorMessage(extractServerErrorMessage(err));
      });
  };
  const { mutate, isLoading } = useMutation(id => chatgenieAPI.deleteIntent(id), {
    onSuccess: () => {
      getData({ queryString: tableQueryState.current });
      setConfirmDeleteData({
        id: null,
        isConfirming: false
      });
    },
    onError(err) {
      toast.error(`Failed to delete intent: ${err?.response?.data?.message || err?.message}`);
    }
  });

  const { mutate: addAllTemplates, isLoading: isAddingAllTemplates } = useMutation(
    () => chatgenieAPI.addAllTemplates(),
    {
      onSuccess: res => {
        const message = res.data.success
          ? 'All templates were added successfully.'
          : 'Intent templates have already been added.';
        toast.success(message);
        setTemplateModalOpen(false);
        getData({ queryString: tableQueryState.current });
      },
      onError(err) {
        toast.error(
          `Failed to add intent templates: ${err?.response?.data?.message || err?.message}`
        );
      }
    }
  );

  const history = useHistory();
  const onEditClick = row => {
    history.push(`/dashboard/magic/agent/update/${row.intentId || row.id}`);
  };
  const onTemplateSubmit = template => {
    history.push({
      pathname: '/dashboard/magic/agent/create',
      state: { template }
    });
  };

  const onDeleteClick = row => {
    setConfirmDeleteData({
      id: row.id,
      isConfirming: true
    });
  };

  const columns = useMemo(() => getColumns({ onEditClick, onDeleteClick }), []);
  const handleOnFilterChange = newFilters => {
    setFilters(newFilters);
    getData({ queryString: tableQueryState.current, passedFilters: newFilters });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    tableQueryState.current = '';
  };

  const onBrowseTemplates = () => {
    setTemplateModalOpen(true);
  };

  const onAddTemplates = () => {
    addAllTemplates();
  };

  return (
    <>
      <AgentHeader>
        <h5>Service Catalog</h5>
        <Typography.Paragraph>
          Magic Agent will use this service catalog to trigger the scenario. You can edit or disable
          any item, you can also define the scenario per client.
        </Typography.Paragraph>
        <MasterIntent />
        <ListHeader>
          <Typography.Title level={5}>
            <Space>
              <span role="img" aria-label="books">
                📚
              </span>
              Your Intents
            </Space>
          </Typography.Title>
          <div>
            <Filters filters={filters} onChange={handleOnFilterChange} />
            <Button onClick={() => setTemplateModalOpen(true)}>Browse templates</Button>
            <Button
              onClick={() => {
                history.push('/dashboard/magic/agent/create');
              }}
              type="primary"
            >
              Create Intent
            </Button>
          </div>
        </ListHeader>
      </AgentHeader>
      {isPlaceholderVisible ? (
        <EmptyListPlaceholder
          onBrowseTemplates={onBrowseTemplates}
          isAddingAllTemplates={isAddingAllTemplates}
          onAddAllTemplates={onAddTemplates}
        />
      ) : (
        <Table
          isSearch
          showTopPanelMeta={false}
          getData={getData}
          isLoading={!!isFetching}
          error={errorMessage}
          columns={columns}
          rows={rows}
          onResetClick={handleReset}
          showResetButton={showResetButton}
        />
      )}
      <IntentTemplatesModal
        isOpen={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSubmit={onTemplateSubmit}
        isAddingAllTemplates={isAddingAllTemplates}
        onAddAllTemplates={onAddTemplates}
      />
      <ConfirmModal
        isOpen={confirmDeleteData.isConfirming}
        toggleHandler={() => setConfirmDeleteData({ id: null, isConfirming: false })}
        title="Delete intent"
        description={<span>Are you sure you want to delete this intent?</span>}
        modalType="error"
        cancelText="Cancel"
        submitText="Yes"
        isLoading={isLoading}
        onSubmit={() => mutate(confirmDeleteData.id)}
      />
    </>
  );
};

export default Agent;
